import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowRight,
  Bell,
  Building2,
  Camera,
  Check,
  ChevronRight,
  CircleHelp,
  Clock3,
  Coins,
  Crown,
  Gift,
  LoaderCircle,
  MapPin,
  Pencil,
  QrCode,
  ReceiptText,
  RefreshCw,
  Search,
  ShieldCheck,
  Sparkles,
  TicketCheck,
  Upload,
  WalletCards,
  X,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { type SyntheticEvent, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import {
  type CustomerActivityItem,
  type CustomerMembership,
  type CustomerPreferences,
  type CustomerProfileDetail,
  type CustomerPunchCardProgress,
  type MarketplaceBusiness,
  type Redemption,
  type Reward,
  type SessionUser,
  claimPreparedCustomerRedemption,
  createCustomerSupportCase,
  customerConfirmCheckout,
  fetchCustomerActivity,
  fetchCustomerHome,
  fetchCustomerPreferences,
  fetchCustomerPunchCards,
  fetchCustomerRewardCoupon,
  fetchCustomerWallet,
  fetchCustomerMeroCredits,
  fetchMarketplaceBusinesses,
  fetchPublicRewards,
  messageFromError,
  prepareCustomerRedemption,
  redeemCheckoutClaim,
  submitReceipt,
  updateCustomerProfileDetail,
  updateCustomerPreferences,
  uploadCustomerProfileImage,
} from "../api";
import { DigitalCardView } from "../experience/LoyaltyExperience";
import { AppAlert } from "../components/AppAlertDialog";
import { PlatformTierCard } from "../components/PlatformTierCard";
import { isCouponAvailable, redemptionStatusLabel } from "./redemption-coupon";
import "./customer-experience.css";

const number = new Intl.NumberFormat("en-US");
const date = new Intl.DateTimeFormat("en-NP", { month: "short", day: "numeric", year: "numeric" });
const money = (minor: number, currency = "NPR") =>
  new Intl.NumberFormat("en-NP", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(minor / 100);
const creditMoney = (minor: number) =>
  new Intl.NumberFormat("en-NP", {
    style: "currency",
    currency: "NPR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(minor / 100);
const meroCredits = (minor: number) =>
  new Intl.NumberFormat("en-NP", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(minor / 100);
const formatDate = (value: string | null) => (value ? date.format(new Date(value)) : "No expiry");
const initials = (value: string) =>
  value
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

function CustomerLoading({ label = "Loading your perks" }: { label?: string }) {
  return (
    <div className="cx-state">
      <LoaderCircle className="cx-spin" />
      <strong>{label}</strong>
      <span>Keeping each business wallet separate and secure.</span>
    </div>
  );
}

function CustomerError({ message, retry }: { message: string; retry: () => void }) {
  return (
    <>
      <AppAlert message={message} />
      <div className="cx-state cx-state--error">
        <CircleHelp />
        <strong>We couldn&apos;t open this area</strong>
        <span>Try again when you are ready.</span>
        <button className="button button--outline button--compact" type="button" onClick={retry}>
          <RefreshCw /> Try again
        </button>
      </div>
    </>
  );
}

function EmptyState({
  icon,
  title,
  detail,
  action,
}: {
  icon: React.ReactNode;
  title: string;
  detail: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="cx-empty">
      <span>{icon}</span>
      <h3>{title}</h3>
      <p>{detail}</p>
      {action}
    </div>
  );
}

function TierCard({
  tier,
}: {
  tier: Awaited<ReturnType<typeof fetchCustomerHome>>["tierSummary"];
}) {
  const nextMinimum = tier.nextTier?.minimumTierPoints ?? tier.activeTierPoints;
  const progress = tier.nextTier
    ? Math.min(100, (tier.activeTierPoints / Math.max(1, nextMinimum)) * 100)
    : 100;
  return (
    <PlatformTierCard
      className="cx-tier-overview"
      tierCode={tier.tier?.code}
      tierName={tier.tier?.name}
      variant="horizontal"
      loading="eager"
    >
      <div className="cx-tier-overview__copy">
        <p className="cx-kicker">YOUR GLOBAL TIER</p>
        <h2>{tier.tier?.name ?? "Mero"}</h2>
        <p>{number.format(tier.activeTierPoints)} active Tier Points</p>
        <div
          className="cx-progress"
          role="progressbar"
          aria-label="Tier progress"
          aria-valuemin={0}
          aria-valuemax={nextMinimum}
          aria-valuenow={tier.activeTierPoints}
        >
          <span style={{ width: `${String(progress)}%` }} />
        </div>
        <strong>
          {tier.nextTier
            ? `${number.format(tier.pointsToNextTier ?? 0)} points to ${tier.nextTier.name}`
            : "You reached the highest tier"}
        </strong>
        <small>Tier Points build status and are never spent.</small>
      </div>
    </PlatformTierCard>
  );
}

function MemberCode() {
  return (
    <Link className="cx-member-code" to="/account/card">
      <span>
        <QrCode />
      </span>
      <div>
        <small>YOUR SECURE MEMBER QR</small>
        <strong>Scan once. Earn both.</strong>
        <p>Open a short-lived, one-time QR only when staff is ready to scan.</p>
      </div>
      <ChevronRight />
    </Link>
  );
}

function WalletStrip({ membership }: { membership: CustomerMembership }) {
  return (
    <Link
      className="cx-wallet-strip"
      to={`/account/wallet?business=${encodeURIComponent(membership.tenant.slug)}`}
      style={{ "--wallet-accent": membership.tenant.primaryColor } as React.CSSProperties}
    >
      <span className="cx-business-mark">
        {membership.tenant.logoUrl ? (
          <img src={membership.tenant.logoUrl} alt="" />
        ) : (
          initials(membership.tenant.businessName)
        )}
      </span>
      <div>
        <small>{membership.program.name}</small>
        <strong>{membership.tenant.businessName}</strong>
        <p>
          {membership.redemption && membership.redemption.redeemableValueMinor > 0
            ? `${creditMoney(membership.redemption.redeemableValueMinor)} available to use here`
            : membership.availableRewards
            ? `${String(membership.availableRewards)} perks ready`
            : "Keep earning at this business"}
        </p>
      </div>
      <div className="cx-wallet-strip__balance">
        <strong>{number.format(membership.pointsBalance)}</strong>
        <small>points</small>
        <ChevronRight />
      </div>
    </Link>
  );
}

function OfferCard({
  reward,
  membership,
  select,
}: {
  reward: Reward;
  membership: CustomerMembership | undefined;
  select: (reward: Reward) => void;
}) {
  const affordable = Boolean(membership && membership.pointsBalance >= reward.pointsCost);
  const displayTheme = (reward.mechanismConfig?.displayTheme ?? "CRIMSON").toLowerCase();
  return (
    <article className={`cx-offer-card cx-offer-card--${displayTheme}`}>
      <div className="cx-offer-card__media">
        {reward.imageUrl ? (
          <img src={reward.imageUrl} alt="" />
        ) : (
          <div>
            <Gift />
            <span>{reward.tenantName}</span>
          </div>
        )}
        <span>{reward.featured ? "Featured" : reward.category.replaceAll("_", " ")}</span>
      </div>
      <div className="cx-offer-card__body">
        <div className="cx-offer-card__merchant">
          <span>{initials(reward.tenantName)}</span>
          <p>{reward.tenantName}</p>
        </div>
        <h3>{reward.title}</h3>
        <p>{reward.description}</p>
        <div className="cx-offer-card__meta">
          <strong>
            <Coins /> {number.format(reward.pointsCost)}
          </strong>
          <span>{reward.expiresAt ? `Ends ${formatDate(reward.expiresAt)}` : "Ongoing"}</span>
        </div>
        <button
          className={`button ${affordable ? "button--primary" : "button--outline"}`}
          type="button"
          onClick={() => {
            select(reward);
          }}
        >
          {membership
            ? affordable
              ? "Redeem reward"
              : `${number.format(reward.pointsCost - membership.pointsBalance)} more points`
            : "Join & redeem"}
          <ArrowRight />
        </button>
      </div>
    </article>
  );
}

function ActivityRow({ item }: { item: CustomerActivityItem }) {
  const icon =
    item.kind === "TIER_POINTS" ? (
      <Crown />
    ) : item.kind === "REDEMPTION" ? (
      <TicketCheck />
    ) : item.kind === "RECEIPT_CLAIM" ? (
      <ReceiptText />
    ) : (
      <Coins />
    );
  const delta = item.kind === "TIER_POINTS" ? item.tierPointsDelta : item.rewardPointsDelta;
  return (
    <article className="cx-activity-row">
      <span className={`cx-activity-row__icon cx-activity-row__icon--${item.kind.toLowerCase()}`}>
        {icon}
      </span>
      <div>
        <strong>{item.title}</strong>
        <p>
          {item.tenantName ?? "Mero Perks"} · {item.detail.replaceAll("_", " ").toLocaleLowerCase()}
        </p>
        <small>
          {formatDate(item.occurredAt)} · {item.status.replaceAll("_", " ").toLocaleLowerCase()}
        </small>
      </div>
      {delta !== 0 && (
        <b className={delta > 0 ? "is-positive" : "is-negative"}>
          {delta > 0 ? "+" : ""}
          {number.format(delta)}
          <small>{item.kind === "TIER_POINTS" ? " tier" : " pts"}</small>
        </b>
      )}
    </article>
  );
}

function RewardConfirmation({
  reward,
  membership,
  close,
  confirm,
  pending,
}: {
  reward: Reward;
  membership: CustomerMembership | undefined;
  close: () => void;
  confirm: (mechanismSelectionId?: string) => void;
  pending: boolean;
}) {
  const options =
    reward.mechanismConfig &&
    "options" in reward.mechanismConfig &&
    Array.isArray(reward.mechanismConfig.options)
      ? reward.mechanismConfig.options
      : [];
  const [selection, setSelection] = useState("");
  return (
    <div
      className="cx-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="reward-confirm-title"
    >
      <button
        className="cx-modal__scrim"
        type="button"
        aria-label="Close redemption"
        onClick={close}
      />
      <div className="cx-modal__card">
        <button className="cx-close" type="button" aria-label="Close" onClick={close}>
          <X />
        </button>
        <p className="cx-kicker">CONFIRM REDEMPTION</p>
        <h2 id="reward-confirm-title">{reward.title}</h2>
        <p className="cx-modal__lead">
          Use{" "}
          <strong>
            {number.format(reward.pointsCost)} {reward.tenantName} points
          </strong>
          . Your Mero Tier Points will not change.
        </p>
        <dl className="cx-rule-list">
          <div>
            <dt>Business wallet</dt>
            <dd>
              {membership
                ? `${number.format(membership.pointsBalance)} points available`
                : "A free membership will be created"}
            </dd>
          </div>
          {reward.minimumSpendMinor > 0 && (
            <div>
              <dt>Minimum spend</dt>
              <dd>{money(reward.minimumSpendMinor, reward.currencyCode)}</dd>
            </div>
          )}
          <div>
            <dt>Where</dt>
            <dd>
              {reward.eligibleLocationNames.length
                ? reward.eligibleLocationNames.join(", ")
                : `Participating ${reward.tenantName} locations`}
            </dd>
          </div>
          <div>
            <dt>Valid until</dt>
            <dd>{formatDate(reward.expiresAt)}</dd>
          </div>
          <div>
            <dt>Limit</dt>
            <dd>{String(reward.perMemberLimit)} per member</dd>
          </div>
        </dl>
        {options.length > 0 && (
          <label className="cx-select">
            <span>Choose your perk</span>
            <select
              required
              value={selection}
              onChange={(event) => {
                setSelection(event.target.value);
              }}
            >
              <option value="">Select one</option>
              {options.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        )}
        <div className="cx-terms">
          <ShieldCheck />
          <p>
            {reward.termsText || "Standard business terms and availability apply."} The QR is valid
            only after server confirmation.
          </p>
        </div>
        <div className="cx-modal__actions">
          <button className="button button--quiet" type="button" onClick={close}>
            Not now
          </button>
          <button
            className="button button--primary"
            disabled={pending || (options.length > 0 && !selection)}
            type="button"
            onClick={() => {
              confirm(selection || undefined);
            }}
          >
            {pending ? "Securing reward…" : `Confirm ${number.format(reward.pointsCost)} points`}
            <Check />
          </button>
        </div>
      </div>
    </div>
  );
}

function CouponDialog({
  publicId,
  summary,
  close,
}: {
  publicId: string;
  summary: Redemption;
  close: () => void;
}) {
  const queryClient = useQueryClient();
  const coupon = useQuery({
    queryKey: ["customer-experience", "coupon", publicId],
    queryFn: () => fetchCustomerRewardCoupon(publicId),
  });
  const liveStatus = useQuery({
    queryKey: ["customer-experience", "coupon-status", publicId],
    queryFn: async () =>
      (await fetchCustomerWallet()).redemptions.find((item) => item.publicId === publicId) ??
      summary,
    refetchInterval: 2_500,
    refetchIntervalInBackground: false,
  });
  const current = liveStatus.data ?? coupon.data ?? summary;
  const presentation = coupon.data;
  const available = presentation
    ? isCouponAvailable({ ...presentation, status: current.status })
    : false;
  const redeemed = ["fulfilled", "redeemed"].includes(current.status.toLocaleLowerCase());
  useEffect(() => {
    if (coupon.data && coupon.data.status.toLocaleLowerCase() !== "issued") {
      void queryClient.invalidateQueries({ queryKey: ["customer-experience", "wallet"] });
    }
  }, [coupon.data, queryClient]);
  useEffect(() => {
    const dismiss = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    window.addEventListener("keydown", dismiss);
    return () => {
      window.removeEventListener("keydown", dismiss);
    };
  }, [close]);
  return (
    <div className="cx-modal" role="dialog" aria-modal="true" aria-labelledby="coupon-title">
      <button className="cx-modal__scrim" type="button" aria-label="Close coupon" onClick={close} />
      <div className="cx-modal__card cx-modal__card--coupon">
        <button className="cx-close" type="button" aria-label="Close coupon" onClick={close}>
          <X />
        </button>
        <img src="/brand/mero-brand-logo.png" alt="" />
        <p className="cx-kicker">ONE-TIME REWARD COUPON</p>
        <h2 id="coupon-title">{current.rewardTitle}</h2>
        <p className="cx-coupon-business">
          {current.tenantName} · {number.format(current.pointsSpent)} points
        </p>
        {coupon.isLoading && (
          <div className="cx-coupon-state" role="status">
            <LoaderCircle className="cx-spin" />
            <strong>Securing your coupon…</strong>
          </div>
        )}
        {coupon.isError && (
          <>
            <AppAlert message={messageFromError(coupon.error)} />
            <div className="cx-coupon-state cx-coupon-state--error">
              <CircleHelp />
              <strong>Coupon temporarily unavailable</strong>
              <button
                className="button button--outline button--compact"
                type="button"
                onClick={() => {
                  void coupon.refetch();
                }}
              >
                Try again
              </button>
            </div>
          </>
        )}
        {!coupon.isLoading && !coupon.isError && available && presentation?.qrPayload && (
          <>
            <div className="cx-coupon-qr-frame">
              <QRCodeSVG
                value={presentation.qrPayload}
                size={250}
                level="H"
                bgColor="#ffffff"
                fgColor="#122937"
                aria-label={`One-time QR reward coupon for ${current.rewardTitle}`}
              />
            </div>
            <div className="cx-coupon-live">
              <span />
              <strong>One-time QR ready</strong>
              <small>
                Expires{" "}
                {presentation.qrExpiresAt
                  ? new Intl.DateTimeFormat("en", {
                      hour: "numeric",
                      minute: "2-digit",
                      second: "2-digit",
                    }).format(new Date(presentation.qrExpiresAt))
                  : "soon"}
              </small>
            </div>
            {presentation.manualCode && (
              <div className="cx-coupon-manual">
                <small>Can’t scan?</small>
                <strong>{presentation.manualCode}</strong>
                <span>Ask staff to enter this one-time code.</span>
              </div>
            )}
            <p className="cx-coupon-help">
              Ask the business to scan this QR code or enter the code now. Both expire together, and
              using either one invalidates both.
            </p>
          </>
        )}
        {!coupon.isLoading &&
          !coupon.isError &&
          current.status.toLocaleLowerCase() === "issued" &&
          !available && (
            <div className="cx-coupon-state">
              <ShieldCheck />
              <strong>This QR expired safely</strong>
              <button
                className="button button--primary button--compact"
                type="button"
                onClick={() => {
                  void coupon.refetch();
                }}
              >
                Generate a new QR
              </button>
            </div>
          )}
        {!coupon.isLoading &&
          !coupon.isError &&
          current.status.toLocaleLowerCase() !== "issued" && (
            <div className={`cx-coupon-result${redeemed ? " cx-coupon-result--redeemed" : ""}`}>
              {redeemed ? <Check /> : <TicketCheck />}
              <strong>{redemptionStatusLabel(current.status)}</strong>
              <p>
                {redeemed
                  ? "This coupon was redeemed by the business and cannot be used again."
                  : "This coupon is no longer available for redemption."}
              </p>
            </div>
          )}
        <button className="button button--outline button--full" type="button" onClick={close}>
          Close
        </button>
      </div>
    </div>
  );
}

function RedemptionSuccess({
  redemption,
  close,
  showCoupon,
}: {
  redemption: Redemption;
  close: () => void;
  showCoupon: () => void;
}) {
  return (
    <div
      className="cx-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="redemption-success-title"
    >
      <button
        className="cx-modal__scrim"
        type="button"
        aria-label="Close redemption"
        onClick={close}
      />
      <div className="cx-modal__card cx-modal__card--success">
        <button className="cx-close" type="button" aria-label="Close" onClick={close}>
          <X />
        </button>
        <span className="cx-success-mark">
          <Check />
        </span>
        <p className="cx-kicker">COUPON READY</p>
        <h2 id="redemption-success-title">{redemption.rewardTitle}</h2>
        <p>
          Your one-time coupon is secured in your wallet. Show it only when the business is ready to
          scan and redeem it.
        </p>
        <small>
          {redemption.tenantName} · {number.format(redemption.pointsSpent)} points
        </small>
        <div className="cx-modal__actions">
          <button className="button button--quiet" type="button" onClick={close}>
            Done
          </button>
          <button className="button button--primary" type="button" onClick={showCoupon}>
            <QrCode /> Show coupon
          </button>
        </div>
      </div>
    </div>
  );
}

function HomeView({
  user,
  selectReward,
}: {
  user: SessionUser;
  selectReward: (reward: Reward) => void;
}) {
  const queryClient = useQueryClient();
  const home = useQuery({ queryKey: ["customer-experience", "home"], queryFn: fetchCustomerHome });
  const confirmation = useMutation({
    mutationFn: customerConfirmCheckout,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["customer-experience"] });
    },
  });
  if (home.isLoading) return <CustomerLoading />;
  if (home.isError || !home.data)
    return (
      <CustomerError
        message={messageFromError(home.error)}
        retry={() => {
          void home.refetch();
        }}
      />
    );
  const data = home.data;
  const membershipsByTenant = new Map(
    data.businessWalletSummaries.map((item) => [item.tenant.publicId, item]),
  );
  return (
    <main className="cx-page">
      <section className="cx-welcome">
        <div>
          <p className="cx-kicker">YOUR LOYALTY HOME</p>
          <h1>Namaste, {user.name.split(" ")[0]}</h1>
          <p>One identity. Every business wallet kept clear, useful, and separate.</p>
        </div>
        <div className="cx-notice-count">
          <Bell />
          <span>{String(data.notificationsSummary.unread)}</span>
          <small>timely reminders</small>
        </div>
      </section>
      {data.pendingActions.length > 0 && (
        <section className="cx-attention">
          <div>
            <span>
              <ShieldCheck />
            </span>
            <div>
              <p className="cx-kicker">ACTION NEEDED</p>
              <h2>Confirm your recent purchase</h2>
              <p>
                {data.pendingActions[0]?.tenantName} recorded{" "}
                {money(
                  data.pendingActions[0]?.amountMinor ?? 0,
                  data.pendingActions[0]?.currencyCode,
                )}
                . Confirm it before points are awarded.
              </p>
            </div>
          </div>
          <button
            className="button button--primary"
            disabled={confirmation.isPending}
            type="button"
            onClick={() => {
              const id = data.pendingActions[0]?.publicId;
              if (id) confirmation.mutate(id);
            }}
          >
            {confirmation.isPending ? "Confirming…" : "Yes, this was mine"}
            <Check />
          </button>
        </section>
      )}
      <section className="cx-command">
        <TierCard tier={data.tierSummary} />
        <MemberCode />
      </section>
      {data.urgentItems.length > 0 && (
        <section className="cx-section">
          <header>
            <div>
              <p className="cx-kicker">USE THEM IN TIME</p>
              <h2>Perks expiring soon</h2>
            </div>
            <Bell />
          </header>
          <div className="cx-urgent-grid">
            {data.urgentItems.map((item) => (
              <article key={item.publicId}>
                <span>
                  <Clock3 />
                </span>
                <div>
                  <small>{item.tenant.name}</small>
                  <strong>{item.title}</strong>
                  <p>{item.message}</p>
                </div>
                <b>{formatDate(item.expiresAt)}</b>
              </article>
            ))}
          </div>
        </section>
      )}
      <section className="cx-section">
        <header>
          <div>
            <p className="cx-kicker">YOUR BUSINESS WALLETS</p>
            <h2>Points, exactly where you earned them</h2>
          </div>
          <Link to="/account/wallet">
            View wallet <ArrowRight />
          </Link>
        </header>
        {data.businessWalletSummaries.length ? (
          <div className="cx-wallet-grid">
            {data.businessWalletSummaries.slice(0, 4).map((membership) => (
              <WalletStrip
                key={`${membership.membershipPublicId}-${membership.program.publicId}`}
                membership={membership}
              />
            ))}
          </div>
        ) : (
          <EmptyState
            icon={<WalletCards />}
            title="Your wallet is ready"
            detail="Join a participating business or redeem your first offer to start earning."
            action={
              <Link className="button button--primary button--compact" to="/account/offers">
                Find a perk
              </Link>
            }
          />
        )}
      </section>
      <section className="cx-section">
        <header>
          <div>
            <p className="cx-kicker">PICKED FOR YOU</p>
            <h2>Worth looking forward to</h2>
          </div>
          <Link to="/account/offers">
            Discover all <ArrowRight />
          </Link>
        </header>
        <div className="cx-offer-grid">
          {data.recommendedOffers.slice(0, 4).map((reward) => (
            <OfferCard
              key={reward.publicId}
              reward={reward}
              membership={membershipsByTenant.get(reward.tenantPublicId)}
              select={selectReward}
            />
          ))}
        </div>
      </section>
      <section className="cx-section">
        <header>
          <div>
            <p className="cx-kicker">RECENT ACTIVITY</p>
            <h2>A clear record of every point</h2>
          </div>
          <Link to="/account/activity">
            See all <ArrowRight />
          </Link>
        </header>
        {data.recentActivity.length ? (
          <div className="cx-activity-list">
            {data.recentActivity.slice(0, 5).map((item) => (
              <ActivityRow key={`${item.kind}-${item.publicId}`} item={item} />
            ))}
          </div>
        ) : (
          <EmptyState
            icon={<Sparkles />}
            title="Your journey starts here"
            detail="Qualified purchases, Tier Points, redemptions, and receipt claims will appear together."
          />
        )}
      </section>
    </main>
  );
}

function WalletView() {
  const location = useLocation();
  const wallet = useQuery({
    queryKey: ["customer-experience", "wallet"],
    queryFn: fetchCustomerWallet,
  });
  const credits = useQuery({
    queryKey: ["mero-credits", "customer"],
    queryFn: fetchCustomerMeroCredits,
  });
  const business = new URLSearchParams(location.search).get("business");
  if (wallet.isLoading) return <CustomerLoading label="Opening your wallet" />;
  if (wallet.isError || !wallet.data)
    return (
      <CustomerError
        message={messageFromError(wallet.error)}
        retry={() => {
          void wallet.refetch();
        }}
      />
    );
  const memberships = business
    ? wallet.data.memberships.filter((item) => item.tenant.slug === business)
    : wallet.data.memberships;
  const totalRewardPoints = wallet.data.memberships.reduce(
    (total, membership) => total + membership.pointsBalance,
    0,
  );
  return (
    <main className="cx-page">
      <section className="cx-page-heading">
        <div>
          <p className="cx-kicker">MY WALLET</p>
          <h1>Every balance, clearly together</h1>
          <p>
            See your total Reward Points, each business balance, and your platform-wide Mero Credits
            without mixing the ledgers.
          </p>
        </div>
        <WalletCards />
      </section>
      <section className="cx-wallet-overview" aria-label="Wallet balances">
        <article className="cx-wallet-overview__points">
          <span>
            <WalletCards />
          </span>
          <div>
            <small>TOTAL REWARD POINTS</small>
            <strong>{number.format(totalRewardPoints)}</strong>
            <p>Combined view of {String(wallet.data.memberships.length)} business wallets</p>
          </div>
        </article>
        <article className="cx-wallet-overview__credits">
          <span>
            <Coins />
          </span>
          <div>
            <small>MERO CREDITS</small>
            <strong>
              {credits.isLoading
                ? "Loading…"
                : credits.data
                  ? meroCredits(credits.data.wallet.totalBalanceMinor)
                  : "Unavailable"}
            </strong>
            {credits.data ? (
              <p>
                {meroCredits(credits.data.breakdown.purchasedMinor)} purchased ·{" "}
                {meroCredits(credits.data.breakdown.promotionalMinor)} promotional
              </p>
            ) : credits.isError ? (
              <button
                type="button"
                onClick={() => {
                  void credits.refetch();
                }}
              >
                Try again
              </button>
            ) : (
              <p>A separate platform-wide Mero Credits balance</p>
            )}
          </div>
        </article>
      </section>
      <section className="cx-section">
        <header>
          <div>
            <p className="cx-kicker">BALANCE BREAKDOWN</p>
            <h2>Reward Points by business</h2>
            <p>Points remain redeemable only with the business that issued them.</p>
          </div>
          <Link to="/account/offers">
            Find offers <ArrowRight />
          </Link>
        </header>
        {memberships.length ? (
          <div className="cx-wallet-detail-grid">
            {memberships.map((membership) => (
              <article
                className="cx-wallet-detail"
                key={`${membership.membershipPublicId}-${membership.program.publicId}`}
              >
                <header
                  style={
                    { "--wallet-accent": membership.tenant.primaryColor } as React.CSSProperties
                  }
                >
                  <span>
                    {membership.tenant.logoUrl ? (
                      <img src={membership.tenant.logoUrl} alt="" />
                    ) : (
                      initials(membership.tenant.businessName)
                    )}
                  </span>
                  <div>
                    <small>{membership.program.name}</small>
                    <h2>{membership.tenant.businessName}</h2>
                  </div>
                  <Building2 />
                </header>
                <div className="cx-wallet-detail__balance">
                  <strong>{number.format(membership.pointsBalance)}</strong>
                  <span>available points</span>
                  {membership.redemption && <small>{creditMoney(membership.redemption.redeemableValueMinor)} redeemable at {membership.tenant.businessName}</small>}
                </div>
                <div className="cx-wallet-detail__stats">
                  <div>
                    <small>Perks ready</small>
                    <strong>{String(membership.availableRewards)}</strong>
                  </div>
                  <div>
                    <small>Expiring in 30 days</small>
                    <strong>{number.format(membership.expiringPoints)}</strong>
                  </div>
                  <div>
                    <small>Member since</small>
                    <strong>{formatDate(membership.joinedAt)}</strong>
                  </div>
                </div>
                {membership.nextPointsExpiry && (
                  <p className="cx-expiry">
                    <Clock3 /> {number.format(membership.expiringPoints)} points expire{" "}
                    {formatDate(membership.nextPointsExpiry)}
                  </p>
                )}
                {membership.redemption && <div className="cx-wallet-redemption"><div><small>Redemption rate</small><strong>{number.format(membership.redemption.pointsPerUnit)} points = {creditMoney(membership.redemption.valuePerUnitMinor)}</strong><span>This balance is locked to {membership.tenant.businessName} and is applied by staff during checkout.</span></div><Link className="button button--primary button--compact" to={`/account/card?business=${encodeURIComponent(membership.tenant.slug)}`}><QrCode /> Use at this business</Link></div>}
                <Link
                  className="button button--outline button--full"
                  to={`/account/offers?business=${encodeURIComponent(membership.tenant.slug)}`}
                >
                  Explore {membership.tenant.businessName} rewards <ArrowRight />
                </Link>
              </article>
            ))}
          </div>
        ) : (
          <EmptyState
            icon={<WalletCards />}
            title="No business wallets yet"
            detail="Discover an offer from a participating business to join its loyalty program."
            action={
              <Link className="button button--primary" to="/account/offers">
                Discover rewards
              </Link>
            }
          />
        )}
      </section>
    </main>
  );
}

function MyRewardsView() {
  const wallet = useQuery({
    queryKey: ["customer-experience", "wallet"],
    queryFn: fetchCustomerWallet,
  });
  const punchCards = useQuery({
    queryKey: ["customer-experience", "punch-cards"],
    queryFn: fetchCustomerPunchCards,
  });
  const [couponId, setCouponId] = useState<string | null>(null);
  if (wallet.isLoading) return <CustomerLoading label="Opening your rewards" />;
  if (wallet.isError || !wallet.data)
    return (
      <CustomerError
        message={messageFromError(wallet.error)}
        retry={() => {
          void wallet.refetch();
        }}
      />
    );
  const selectedCoupon = couponId
    ? (wallet.data.redemptions.find((item) => item.publicId === couponId) ?? null)
    : null;
  const readyCoupons = wallet.data.redemptions.filter(
    (item) => item.status.toLocaleLowerCase() === "issued",
  ).length;
  return (
    <main className="cx-page">
      <section className="cx-page-heading">
        <div>
          <p className="cx-kicker">MY REWARDS</p>
          <h1>Perks ready when you are</h1>
          <p>Find available benefits, issued rewards, and secure one-time coupons in one place.</p>
        </div>
        <div className="cx-reward-count">
          <Gift />
          <strong>{String(wallet.data.entitlements.length + readyCoupons)}</strong>
          <span>ready to use</span>
        </div>
      </section>
      {(punchCards.data ?? []).length > 0 && (
        <section className="cx-section">
          <header>
            <div>
              <p className="cx-kicker">DIGITAL PUNCH CARDS</p>
              <h2>Your progress</h2>
              <p>Verified purchases add punches automatically. A secure coupon appears when a card is complete.</p>
            </div>
            <Sparkles />
          </header>
          <div className="cx-punch-grid">
            {(punchCards.data ?? []).map((card: CustomerPunchCardProgress) => (
              <article key={card.rewardPublicId}>
                <div><small>{card.businessName}</small><h3>{card.rewardName}</h3></div>
                <strong>{card.currentPunches} / {card.requiredPunches}</strong>
                <progress value={card.currentPunches} max={card.requiredPunches} aria-label={`${String(card.currentPunches)} of ${String(card.requiredPunches)} punches`} />
                <p>{card.punchesRemaining} more {card.punchMode === "VISIT" ? "visit(s)" : "item(s)"} to unlock <b>{card.rewardItemLabel}</b>.</p>
              </article>
            ))}
          </div>
        </section>
      )}
      <section className="cx-section">
        <header>
          <div>
            <p className="cx-kicker">AVAILABLE REWARDS</p>
            <h2>Issued perks</h2>
          </div>
          <TicketCheck />
        </header>
        {wallet.data.entitlements.length ? (
          <div className="cx-entitlement-grid">
            {wallet.data.entitlements.map((item) => (
              <article key={item.publicId}>
                <span>
                  <Gift />
                </span>
                <div>
                  <small>{item.tenant.businessName}</small>
                  <h3>{item.reward.name}</h3>
                  <p>{item.reward.description}</p>
                  <b>{item.expiresAt ? `Use by ${formatDate(item.expiresAt)}` : "No expiry"}</b>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <EmptyState
            icon={<Gift />}
            title="No issued perks yet"
            detail="Birthday, anniversary, referral, and campaign perks will appear here when you qualify."
          />
        )}
      </section>
      <section className="cx-section">
        <header>
          <div>
            <p className="cx-kicker">COUPON WALLET</p>
            <h2>Rewards ready to scan</h2>
            <p>Coupon codes stay hidden until you choose to show one.</p>
          </div>
        </header>
        {wallet.data.redemptions.length ? (
          <div className="cx-redemption-list">
            {wallet.data.redemptions.map((item) => {
              const available = item.status.toLocaleLowerCase() === "issued";
              return (
                <article
                  key={item.publicId}
                  className={available ? "" : "cx-redemption-list__complete"}
                >
                  <span className={`cx-status cx-status--${item.status.toLocaleLowerCase()}`}>
                    {redemptionStatusLabel(item.status)}
                  </span>
                  <div>
                    <strong>{item.rewardTitle}</strong>
                    <p>
                      {item.tenantName} · {number.format(item.pointsSpent)} points
                    </p>
                    <small>
                      {available
                        ? `Requested ${formatDate(item.createdAt)}`
                        : `${redemptionStatusLabel(item.status)} ${formatDate(item.fulfilledAt ?? item.createdAt)}`}
                    </small>
                  </div>
                  {available ? (
                    <button
                      className="button button--primary button--compact cx-show-coupon"
                      type="button"
                      onClick={() => {
                        setCouponId(item.publicId);
                      }}
                    >
                      <QrCode /> Show coupon
                    </button>
                  ) : (
                    <span className="cx-coupon-closed">
                      <Check /> {redemptionStatusLabel(item.status)}
                    </span>
                  )}
                </article>
              );
            })}
          </div>
        ) : (
          <EmptyState
            icon={<TicketCheck />}
            title="No coupons yet"
            detail="Rewards you request will appear here without exposing their one-time codes."
          />
        )}
      </section>
      {selectedCoupon && (
        <CouponDialog
          publicId={selectedCoupon.publicId}
          summary={selectedCoupon}
          close={() => {
            setCouponId(null);
          }}
        />
      )}
    </main>
  );
}

function BusinessResultCard({ business }: { business: MarketplaceBusiness }) {
  const location = business.locations[0];
  return (
    <article className="cx-business-result">
      <span style={{ "--business-accent": business.primaryColor } as React.CSSProperties}>
        {business.logoUrl ? <img src={business.logoUrl} alt="" /> : initials(business.name)}
      </span>
      <div>
        <small>{business.businessType.replaceAll("_", " ")}</small>
        <h3>{business.name}</h3>
        <p>
          <MapPin />{" "}
          {location
            ? [location.city, location.district].filter(Boolean).join(", ") || location.addressLine1
            : "Locations coming soon"}
        </p>
      </div>
      <div className="cx-business-result__action">
        <strong>{String(business.rewardCount)}</strong>
        <small>offers</small>
        <Link to={`/account/offers?business=${encodeURIComponent(business.slug)}`}>View</Link>
      </div>
    </article>
  );
}

function DiscoverView({ selectReward }: { selectReward: (reward: Reward) => void }) {
  const location = useLocation();
  const [search, setSearch] = useState("");
  const rewards = useQuery({ queryKey: ["public-rewards"], queryFn: fetchPublicRewards });
  const businesses = useQuery({
    queryKey: ["customer-experience", "business-search", search],
    queryFn: () => {
      const term = search.trim();
      return fetchMarketplaceBusinesses(term ? { search: term } : {});
    },
  });
  const home = useQuery({ queryKey: ["customer-experience", "home"], queryFn: fetchCustomerHome });
  const business = new URLSearchParams(location.search).get("business");
  const selectedRewardId = new URLSearchParams(location.search).get("reward");
  const filtered = useMemo(
    () =>
      (rewards.data ?? []).filter((reward) => {
        const term = search.trim().toLocaleLowerCase();
        return (
          (!business || reward.tenantSlug === business) &&
          (!term ||
            `${reward.title} ${reward.description} ${reward.tenantName} ${reward.category}`
              .toLocaleLowerCase()
              .includes(term))
        );
      }),
    [business, rewards.data, search],
  );
  const memberships = new Map(
    (home.data?.businessWalletSummaries ?? []).map((item) => [item.tenant.publicId, item]),
  );
  const matchingBusinesses = (businesses.data?.items ?? [])
    .filter((item) => !business || item.slug === business)
    .slice(0, 6);
  useEffect(() => {
    const selected = (rewards.data ?? []).find((item) => item.publicId === selectedRewardId);
    if (selected) selectReward(selected);
  }, [rewards.data, selectReward, selectedRewardId]);
  if (rewards.isLoading) return <CustomerLoading label="Finding rewards" />;
  if (rewards.isError)
    return (
      <CustomerError
        message={messageFromError(rewards.error)}
        retry={() => {
          void rewards.refetch();
        }}
      />
    );
  return (
    <main className="cx-page">
      <section className="cx-discover-hero">
        <div>
          <p className="cx-kicker">OFFERS &amp; COUPONS</p>
          <h1>Find a business. Pick your next perk.</h1>
          <p>Search businesses and browse their available rewards without leaving this screen.</p>
        </div>
        <div className="cx-search">
          <Search />
          <label>
            <span className="sr-only">Search businesses and rewards</span>
            <input
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
              }}
              placeholder="Search businesses, offers, categories or places…"
            />
          </label>
          {search && (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => {
                setSearch("");
              }}
            >
              <X />
            </button>
          )}
        </div>
      </section>
      {business && (
        <div className="cx-filter-note">
          <Building2 /> Showing rewards from <strong>{filtered[0]?.tenantName ?? business}</strong>
          <Link to="/account/offers">Clear</Link>
        </div>
      )}
      <section className="cx-section cx-business-search-results">
        <header>
          <div>
            <p className="cx-kicker">PARTICIPATING BUSINESSES</p>
            <h2>{search.trim() ? "Businesses matching your search" : "Places with Mero Perks"}</h2>
          </div>
          <Building2 />
        </header>
        {businesses.isLoading ? (
          <CustomerLoading label="Finding businesses" />
        ) : businesses.isError ? (
          <CustomerError
            message={messageFromError(businesses.error)}
            retry={() => {
              void businesses.refetch();
            }}
          />
        ) : matchingBusinesses.length ? (
          <div className="cx-business-result-grid">
            {matchingBusinesses.map((item) => (
              <BusinessResultCard key={item.publicId} business={item} />
            ))}
          </div>
        ) : (
          <EmptyState
            icon={<Building2 />}
            title="No matching businesses"
            detail="Try a broader business, category, or location search."
          />
        )}
      </section>
      <section className="cx-offers-heading">
        <div>
          <p className="cx-kicker">AVAILABLE OFFERS</p>
          <h2>Rewards and coupons</h2>
        </div>
        <span>{String(filtered.length)} found</span>
      </section>
      <div className="cx-offer-grid cx-offer-grid--all">
        {filtered.map((reward) => (
          <OfferCard
            key={reward.publicId}
            reward={reward}
            membership={memberships.get(reward.tenantPublicId)}
            select={selectReward}
          />
        ))}
      </div>
      {!filtered.length && (
        <EmptyState
          icon={<Search />}
          title="No matching perks"
          detail="Try a broader search or clear the business filter."
        />
      )}
    </main>
  );
}

function ActivityView() {
  const queryClient = useQueryClient();
  const activity = useQuery({
    queryKey: ["customer-experience", "activity"],
    queryFn: fetchCustomerActivity,
  });
  const [claimCode, setClaimCode] = useState("");
  const [receiptTenant, setReceiptTenant] = useState("");
  const [receiptAmount, setReceiptAmount] = useState("");
  const [receiptReference, setReceiptReference] = useState("");
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [message, setMessage] = useState("");
  const claim = useMutation({
    mutationFn: redeemCheckoutClaim,
    onSuccess: async () => {
      setClaimCode("");
      setMessage("Purchase claimed. Points will appear after confirmation.");
      await queryClient.invalidateQueries({ queryKey: ["customer-experience"] });
    },
    onError: (error) => {
      setMessage(messageFromError(error));
    },
  });
  const receipt = useMutation({
    mutationFn: submitReceipt,
    onSuccess: async () => {
      setReceiptAmount("");
      setReceiptReference("");
      setReceiptFile(null);
      setMessage("Receipt submitted for review.");
      await queryClient.invalidateQueries({ queryKey: ["customer-experience"] });
    },
    onError: (error) => {
      setMessage(messageFromError(error));
    },
  });
  if (activity.isLoading) return <CustomerLoading label="Building your activity timeline" />;
  if (activity.isError)
    return (
      <CustomerError
        message={messageFromError(activity.error)}
        retry={() => {
          void activity.refetch();
        }}
      />
    );
  return (
    <main className="cx-page">
      <section className="cx-page-heading">
        <div>
          <p className="cx-kicker">UNIFIED ACTIVITY</p>
          <h1>Every point has a reason</h1>
          <p>
            Business points, platform Tier Points, redemptions, reversals, and receipt claims in one
            auditable timeline.
          </p>
        </div>
        <Clock3 />
      </section>
      {message && (
        <div className="cx-message" role="status">
          {message}
        </div>
      )}
      <section className="cx-earn-grid">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (/^\d{6}$/.test(claimCode)) claim.mutate(claimCode);
          }}
        >
          <span>
            <QrCode />
          </span>
          <div>
            <p className="cx-kicker">CASH PURCHASE</p>
            <h2>Enter checkout code</h2>
            <p>Use the one-time code shown by the business.</p>
          </div>
          <label>
            <span>Six-digit code</span>
            <input
              inputMode="numeric"
              pattern="\d{6}"
              maxLength={6}
              value={claimCode}
              onChange={(event) => {
                setClaimCode(event.target.value.replace(/\D/g, ""));
              }}
              placeholder="000000"
            />
          </label>
          <button
            className="button button--primary"
            disabled={claim.isPending || claimCode.length !== 6}
            type="submit"
          >
            {claim.isPending ? "Claiming…" : "Claim purchase"}
            <ArrowRight />
          </button>
        </form>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            receipt.mutate({
              tenantSlug: receiptTenant.trim(),
              grossAmountMinor: Math.round(Number(receiptAmount) * 100),
              ...(receiptReference.trim() ? { merchantReference: receiptReference.trim() } : {}),
              ...(receiptFile ? { receipt: receiptFile } : {}),
            });
          }}
        >
          <span>
            <ReceiptText />
          </span>
          <div>
            <p className="cx-kicker">MISSING POINTS</p>
            <h2>Submit a receipt</h2>
            <p>Ask the business to verify a purchase that is missing.</p>
          </div>
          <div className="cx-form-row">
            <label>
              <span>Business slug</span>
              <input
                required
                value={receiptTenant}
                onChange={(event) => {
                  setReceiptTenant(event.target.value.toLocaleLowerCase());
                }}
                placeholder="himal-brew-house"
              />
            </label>
            <label>
              <span>Amount (NPR)</span>
              <input
                required
                min="1"
                step="0.01"
                type="number"
                value={receiptAmount}
                onChange={(event) => {
                  setReceiptAmount(event.target.value);
                }}
                placeholder="850"
              />
            </label>
          </div>
          <label>
            <span>Receipt reference</span>
            <input
              value={receiptReference}
              onChange={(event) => {
                setReceiptReference(event.target.value);
              }}
              placeholder="Optional"
            />
          </label>
          <label>
            <span>Receipt image or PDF</span>
            <input
              accept="image/jpeg,image/png,image/webp,application/pdf"
              type="file"
              onChange={(event) => {
                setReceiptFile(event.target.files?.[0] ?? null);
              }}
            />
          </label>
          <button className="button button--outline" disabled={receipt.isPending} type="submit">
            {receipt.isPending ? "Submitting…" : "Submit for review"}
            <ReceiptText />
          </button>
        </form>
      </section>
      <section className="cx-section">
        <header>
          <div>
            <p className="cx-kicker">YOUR LEDGER</p>
            <h2>Latest activity</h2>
          </div>
          <button
            className="button button--quiet button--compact"
            type="button"
            onClick={() => {
              void activity.refetch();
            }}
          >
            <RefreshCw /> Refresh
          </button>
        </header>
        {activity.data?.length ? (
          <div className="cx-activity-list">
            {activity.data.map((item) => (
              <ActivityRow key={`${item.kind}-${item.publicId}`} item={item} />
            ))}
          </div>
        ) : (
          <EmptyState
            icon={<Sparkles />}
            title="No activity yet"
            detail="Your first verified purchase will start this timeline."
          />
        )}
      </section>
    </main>
  );
}

function ProfileView({ user }: { user: SessionUser }) {
  const queryClient = useQueryClient();
  const preferences = useQuery({
    queryKey: ["customer-experience", "preferences"],
    queryFn: fetchCustomerPreferences,
  });
  const home = useQuery({ queryKey: ["customer-experience", "home"], queryFn: fetchCustomerHome });
  const [draft, setDraft] = useState<CustomerPreferences | null>(null);
  const [profileDraft, setProfileDraft] = useState<CustomerProfileDetail | null>(null);
  const [editingProfile, setEditingProfile] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [photoUploading, setPhotoUploading] = useState(false);
  useEffect(() => {
    if (preferences.data) setDraft(preferences.data);
  }, [preferences.data]);
  const save = useMutation({
    mutationFn: updateCustomerPreferences,
    onSuccess: async (value) => {
      setDraft(value);
      setMessage("Preferences saved.");
      await queryClient.invalidateQueries({ queryKey: ["customer-experience", "preferences"] });
    },
    onError: (error) => {
      setMessage(messageFromError(error));
    },
  });
  const profileSave = useMutation({
    mutationFn: updateCustomerProfileDetail,
    onSuccess: async (result) => {
      setEditingProfile(false);
      setProfileDraft(null);
      setMessage(
        result.verificationRequired
          ? "Profile saved. Verify the link sent to your new email before signing in again."
          : "Profile details saved.",
      );
      await queryClient.invalidateQueries({ queryKey: ["customer-experience", "home"] });
    },
    onError: (error) => {
      setMessage(messageFromError(error));
    },
  });
  if (preferences.isLoading || home.isLoading)
    return <CustomerLoading label="Opening your profile" />;
  if (preferences.isError || home.isError || !draft || !home.data)
    return (
      <CustomerError
        message={messageFromError(preferences.error ?? home.error)}
        retry={() => {
          void Promise.all([preferences.refetch(), home.refetch()]);
        }}
      />
    );
  const profile = home.data.profile;
  const chooseProfilePhoto = async (file: File | undefined) => {
    if (!file || !profileDraft) return;
    setPhotoUploading(true);
    try {
      const profileImageUrl = await uploadCustomerProfileImage(file);
      setProfileDraft({ ...profileDraft, profileImageUrl });
      setMessage("Photo uploaded. Save your profile to apply it.");
    } catch (error) {
      setMessage(messageFromError(error));
    } finally {
      setPhotoUploading(false);
    }
  };
  return (
    <main className="cx-page">
      <section className="cx-profile-hero">
        {profile.profileImageUrl ? (
          <img src={profile.profileImageUrl} alt={`${user.name} profile`} />
        ) : (
          <span>{initials(user.name)}</span>
        )}
        <div>
          <p className="cx-kicker">MERO PERKS MEMBER</p>
          <h1>{user.name}</h1>
          <p>
            {user.email} · Member since {formatDate(profile.createdAt)}
          </p>
        </div>
        <ShieldCheck />
      </section>
      {message && (
        <div className="cx-message" role="status">
          {message}
        </div>
      )}
      <div className="cx-profile-grid">
        <section className="cx-section">
          <header>
            <div>
              <p className="cx-kicker">PROFILE</p>
              <h2>Your details</h2>
            </div>
            <button
              className="button button--outline button--compact"
              type="button"
              onClick={() => {
                if (editingProfile) {
                  setEditingProfile(false);
                  setProfileDraft(null);
                } else {
                  setProfileDraft(profile);
                  setEditingProfile(true);
                }
              }}
            >
              {editingProfile ? <X /> : <Pencil />}
              {editingProfile ? "Cancel" : "Edit profile"}
            </button>
          </header>
          {editingProfile && profileDraft ? (
            <form
              className="cx-profile-form"
              onSubmit={(event) => {
                event.preventDefault();
                const nullable = (value: string | null) => value?.trim() || null;
                profileSave.mutate({
                  firstName: profileDraft.firstName.trim(),
                  lastName: profileDraft.lastName.trim(),
                  email: profileDraft.email.trim(),
                  phone: nullable(profileDraft.phone),
                  birthday: nullable(profileDraft.birthday),
                  addressLine1: nullable(profileDraft.addressLine1),
                  addressLine2: nullable(profileDraft.addressLine2),
                  city: nullable(profileDraft.city),
                  district: nullable(profileDraft.district),
                  province: nullable(profileDraft.province),
                  country: nullable(profileDraft.country),
                  postalCode: nullable(profileDraft.postalCode),
                  profileImageUrl: profileDraft.profileImageUrl,
                });
              }}
            >
              <div className="cx-profile-photo-editor">
                {profileDraft.profileImageUrl ? (
                  <img src={profileDraft.profileImageUrl} alt="Profile preview" />
                ) : (
                  <span>{initials(`${profileDraft.firstName} ${profileDraft.lastName}`)}</span>
                )}
                <div>
                  <label className="button button--outline button--compact">
                    <Upload /> Upload picture
                    <input
                      hidden
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      disabled={photoUploading}
                      onChange={(event) => void chooseProfilePhoto(event.target.files?.[0])}
                    />
                  </label>
                  <label className="button button--outline button--compact">
                    <Camera /> Take picture
                    <input
                      hidden
                      type="file"
                      accept="image/*"
                      capture="user"
                      disabled={photoUploading}
                      onChange={(event) => void chooseProfilePhoto(event.target.files?.[0])}
                    />
                  </label>
                  {profileDraft.profileImageUrl && (
                    <button type="button" className="button button--text button--compact" onClick={() => { setProfileDraft({ ...profileDraft, profileImageUrl: null }); }}>
                      Remove photo
                    </button>
                  )}
                </div>
              </div>
              <div className="cx-form-row">
                <label>
                  <span>First name</span>
                  <input
                    required
                    value={profileDraft.firstName}
                    onChange={(event) => {
                      setProfileDraft({ ...profileDraft, firstName: event.target.value });
                    }}
                  />
                </label>
                <label>
                  <span>Last name</span>
                  <input
                    required
                    value={profileDraft.lastName}
                    onChange={(event) => {
                      setProfileDraft({ ...profileDraft, lastName: event.target.value });
                    }}
                  />
                </label>
              </div>
              <label>
                <span>Email</span>
                <input
                  required
                  type="email"
                  value={profileDraft.email}
                  onChange={(event) => {
                    setProfileDraft({ ...profileDraft, email: event.target.value });
                  }}
                />
                <small>Changing your email requires verification.</small>
              </label>
              <div className="cx-form-row">
                <label>
                  <span>Phone</span>
                  <input
                    type="tel"
                    value={profileDraft.phone ?? ""}
                    onChange={(event) => {
                      setProfileDraft({ ...profileDraft, phone: event.target.value });
                    }}
                  />
                </label>
                <label>
                  <span>Birthday</span>
                  <input
                    type="date"
                    value={profileDraft.birthday ?? ""}
                    onChange={(event) => {
                      setProfileDraft({ ...profileDraft, birthday: event.target.value });
                    }}
                  />
                </label>
              </div>
              <label>
                <span>Address</span>
                <input
                  value={profileDraft.addressLine1 ?? ""}
                  onChange={(event) => {
                    setProfileDraft({ ...profileDraft, addressLine1: event.target.value });
                  }}
                  placeholder="Street address"
                />
              </label>
              <label>
                <span>Address line 2</span>
                <input
                  value={profileDraft.addressLine2 ?? ""}
                  onChange={(event) => {
                    setProfileDraft({ ...profileDraft, addressLine2: event.target.value });
                  }}
                  placeholder="Apartment, suite, or landmark"
                />
              </label>
              <div className="cx-form-row">
                <label>
                  <span>City</span>
                  <input
                    value={profileDraft.city ?? ""}
                    onChange={(event) => {
                      setProfileDraft({ ...profileDraft, city: event.target.value });
                    }}
                  />
                </label>
                <label>
                  <span>District</span>
                  <input
                    value={profileDraft.district ?? ""}
                    onChange={(event) => {
                      setProfileDraft({ ...profileDraft, district: event.target.value });
                    }}
                  />
                </label>
              </div>
              <div className="cx-form-row">
                <label>
                  <span>Province</span>
                  <input
                    value={profileDraft.province ?? ""}
                    onChange={(event) => {
                      setProfileDraft({ ...profileDraft, province: event.target.value });
                    }}
                  />
                </label>
                <label>
                  <span>Postal code</span>
                  <input
                    value={profileDraft.postalCode ?? ""}
                    onChange={(event) => {
                      setProfileDraft({ ...profileDraft, postalCode: event.target.value });
                    }}
                  />
                </label>
              </div>
              <label>
                <span>Country</span>
                <input
                  value={profileDraft.country ?? ""}
                  onChange={(event) => {
                    setProfileDraft({ ...profileDraft, country: event.target.value });
                  }}
                />
              </label>
              <button
                className="button button--primary"
                disabled={profileSave.isPending}
                type="submit"
              >
                <Check /> {profileSave.isPending ? "Saving…" : "Save profile"}
              </button>
            </form>
          ) : (
            <dl className="cx-profile-details">
              <div>
                <dt>Name</dt>
                <dd>
                  {profile.firstName} {profile.lastName}
                </dd>
              </div>
              <div>
                <dt>Email</dt>
                <dd>{profile.email}</dd>
              </div>
              <div>
                <dt>Phone</dt>
                <dd>{profile.phone || "Not provided"}</dd>
              </div>
              <div>
                <dt>Birthday</dt>
                <dd>{profile.birthday ? formatDate(profile.birthday) : "Not provided"}</dd>
              </div>
              <div>
                <dt>Address</dt>
                <dd>
                  {[profile.addressLine1, profile.city, profile.district]
                    .filter(Boolean)
                    .join(", ") || "Not provided"}
                </dd>
              </div>
              <div>
                <dt>Joined businesses</dt>
                <dd>{String(home.data.businessWalletSummaries.length)}</dd>
              </div>
            </dl>
          )}
          <p className="cx-privacy-note">
            <ShieldCheck /> Your identity is global, but businesses only see their own membership
            and activity.
          </p>
        </section>
        <section className="cx-section">
          <header>
            <div>
              <p className="cx-kicker">COMMUNICATIONS</p>
              <h2>You control what arrives</h2>
            </div>
          </header>
          <form
            className="cx-preferences"
            onSubmit={(event) => {
              event.preventDefault();
              save.mutate(draft);
            }}
          >
            <label>
              <input
                type="checkbox"
                checked={draft.serviceNotifications}
                onChange={(event) => {
                  setDraft({ ...draft, serviceNotifications: event.target.checked });
                }}
              />
              <span>
                <strong>Service notifications</strong>
                <small>Security, receipts, expiring points, and account updates.</small>
              </span>
            </label>
            <label>
              <input
                type="checkbox"
                checked={draft.marketingEmail}
                onChange={(event) => {
                  setDraft({ ...draft, marketingEmail: event.target.checked });
                }}
              />
              <span>
                <strong>Email offers</strong>
                <small>Relevant perks from Mero Perks and businesses you joined.</small>
              </span>
            </label>
            <label>
              <input
                type="checkbox"
                checked={draft.marketingPush}
                onChange={(event) => {
                  setDraft({ ...draft, marketingPush: event.target.checked });
                }}
              />
              <span>
                <strong>Push offers</strong>
                <small>Timely mobile alerts. Separate from service notices.</small>
              </span>
            </label>
            <label>
              <input
                type="checkbox"
                checked={draft.marketingSms}
                onChange={(event) => {
                  setDraft({ ...draft, marketingSms: event.target.checked });
                }}
              />
              <span>
                <strong>SMS offers</strong>
                <small>Optional promotional texts.</small>
              </span>
            </label>
            <button className="button button--primary" disabled={save.isPending} type="submit">
              {save.isPending ? "Saving…" : "Save preferences"}
              <Check />
            </button>
          </form>
        </section>
      </div>
      <section className="cx-section">
        <header>
          <div>
            <p className="cx-kicker">HELP & PRIVACY</p>
            <h2>Support that follows the evidence</h2>
          </div>
        </header>
        <div className="cx-support-actions">
          <button
            type="button"
            onClick={() => {
              setSupportOpen(true);
            }}
          >
            <CircleHelp />
            <span>
              <strong>Open a support case</strong>
              <small>Missing points, redemption, receipt, or account issue.</small>
            </span>
            <ChevronRight />
          </button>
          <a href="mailto:privacy@meroperks.com">
            <ShieldCheck />
            <span>
              <strong>Privacy request</strong>
              <small>Access, correction, or deletion questions.</small>
            </span>
            <ChevronRight />
          </a>
        </div>
      </section>
      {supportOpen && (
        <SupportDialog
          close={() => {
            setSupportOpen(false);
          }}
          memberships={home.data.businessWalletSummaries}
          submitted={(value) => {
            setSupportOpen(false);
            setMessage(`Support case ${value.publicId.slice(0, 8)} opened.`);
          }}
        />
      )}
    </main>
  );
}

function SupportDialog({
  close,
  memberships,
  submitted,
}: {
  close: () => void;
  memberships: CustomerMembership[];
  submitted: (value: { publicId: string; status: string; createdAt: string }) => void;
}) {
  const [category, setCategory] = useState<
    "MISSING_POINTS" | "REDEMPTION" | "TRANSACTION" | "RECEIPT" | "ACCOUNT" | "PRIVACY" | "OTHER"
  >("MISSING_POINTS");
  const [tenantSlug, setTenantSlug] = useState("");
  const [subject, setSubject] = useState("");
  const [detail, setDetail] = useState("");
  const [error, setError] = useState("");
  const mutation = useMutation({
    mutationFn: createCustomerSupportCase,
    onSuccess: submitted,
    onError: (caught) => {
      setError(messageFromError(caught));
    },
  });
  const submit = (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    mutation.mutate({ category, subject, message: detail, ...(tenantSlug ? { tenantSlug } : {}) });
  };
  return (
    <div className="cx-modal" role="dialog" aria-modal="true" aria-labelledby="support-title">
      <button
        className="cx-modal__scrim"
        type="button"
        aria-label="Close support form"
        onClick={close}
      />
      <form className="cx-modal__card cx-support-form" onSubmit={submit}>
        <button className="cx-close" type="button" aria-label="Close" onClick={close}>
          <X />
        </button>
        <p className="cx-kicker">CUSTOMER SUPPORT</p>
        <h2 id="support-title">Tell us what happened</h2>
        <p>We will attach only the business context you select.</p>
        {error && (
          <AppAlert
            message={error}
            onDismiss={() => {
              setError("");
            }}
          />
        )}
        <label>
          <span>Topic</span>
          <select
            value={category}
            onChange={(event) => {
              setCategory(event.target.value as typeof category);
            }}
          >
            <option value="MISSING_POINTS">Missing points</option>
            <option value="REDEMPTION">Redemption issue</option>
            <option value="TRANSACTION">Purchase confirmation</option>
            <option value="RECEIPT">Receipt claim</option>
            <option value="ACCOUNT">Account</option>
            <option value="PRIVACY">Privacy</option>
            <option value="OTHER">Something else</option>
          </select>
        </label>
        <label>
          <span>Business (optional)</span>
          <select
            value={tenantSlug}
            onChange={(event) => {
              setTenantSlug(event.target.value);
            }}
          >
            <option value="">Mero Perks platform</option>
            {memberships.map((item) => (
              <option key={item.tenant.publicId} value={item.tenant.slug}>
                {item.tenant.businessName}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Subject</span>
          <input
            required
            minLength={3}
            maxLength={140}
            value={subject}
            onChange={(event) => {
              setSubject(event.target.value);
            }}
          />
        </label>
        <label>
          <span>What happened?</span>
          <textarea
            required
            minLength={10}
            maxLength={4000}
            rows={5}
            value={detail}
            onChange={(event) => {
              setDetail(event.target.value);
            }}
          />
        </label>
        <button
          className="button button--primary button--full"
          disabled={mutation.isPending}
          type="submit"
        >
          {mutation.isPending ? "Opening case…" : "Open support case"}
          <ArrowRight />
        </button>
      </form>
    </div>
  );
}

export function CustomerExperience({ user }: { user: SessionUser }) {
  const location = useLocation();
  const queryClient = useQueryClient();
  const [reward, setReward] = useState<Reward | null>(null);
  const [redemption, setRedemption] = useState<Redemption | null>(null);
  const [newCouponOpen, setNewCouponOpen] = useState(false);
  const [error, setError] = useState("");
  const home = useQuery({ queryKey: ["customer-experience", "home"], queryFn: fetchCustomerHome });
  const redeem = useMutation({
    mutationFn: async ({
      selected,
      mechanismSelectionId,
    }: {
      selected: Reward;
      mechanismSelectionId?: string | undefined;
    }) => {
      const preparation = await prepareCustomerRedemption(selected.publicId, mechanismSelectionId);
      return claimPreparedCustomerRedemption(preparation.token);
    },
    onSuccess: async (value) => {
      setReward(null);
      setRedemption(value);
      setNewCouponOpen(false);
      setError("");
      await queryClient.invalidateQueries({ queryKey: ["customer-experience"] });
    },
    onError: (caught) => {
      setError(messageFromError(caught));
    },
  });
  const path = location.pathname.replace(/\/$/, "");
  if (path === "/account") {
    return (
      <Navigate
        to={
          new URLSearchParams(location.search).has("reward")
            ? `/account/offers${location.search}`
            : "/account/home"
        }
        replace
      />
    );
  }
  const page = path.split("/").at(-1);
  const content =
    page === "home" ? (
      <HomeView user={user} selectReward={setReward} />
    ) : page === "wallet" ? (
      <WalletView />
    ) : page === "rewards" ? (
      <MyRewardsView />
    ) : page === "mero-credits" ? (
      <Navigate to="/account/wallet" replace />
    ) : page === "card" ? (
      <DigitalCardView />
    ) : page === "discover" ? (
      <Navigate to={`/account/offers${location.search}`} replace />
    ) : page === "offers" ? (
      <DiscoverView selectReward={setReward} />
    ) : page === "activity" ? (
      <ActivityView />
    ) : page === "profile" ? (
      <ProfileView user={user} />
    ) : (
      <Navigate to="/account/home" replace />
    );
  const rewardMembership = reward
    ? home.data?.businessWalletSummaries.find(
        (item) => item.tenant.publicId === reward.tenantPublicId,
      )
    : undefined;
  return (
    <>
      {error && (
        <AppAlert
          message={error}
          onDismiss={() => {
            setError("");
          }}
        />
      )}
      {content}
      {reward && (
        <RewardConfirmation
          reward={reward}
          membership={rewardMembership}
          close={() => {
            setReward(null);
            setError("");
          }}
          pending={redeem.isPending}
          confirm={(mechanismSelectionId) => {
            redeem.mutate({ selected: reward, mechanismSelectionId });
          }}
        />
      )}
      {redemption &&
        (newCouponOpen ? (
          <CouponDialog
            publicId={redemption.publicId}
            summary={redemption}
            close={() => {
              setRedemption(null);
              setNewCouponOpen(false);
            }}
          />
        ) : (
          <RedemptionSuccess
            redemption={redemption}
            close={() => {
              setRedemption(null);
            }}
            showCoupon={() => {
              setNewCouponOpen(true);
            }}
          />
        ))}
    </>
  );
}
